'use client';

import React from 'react';
import { useParallax } from 'react-scroll-parallax';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { mergeSx } from '@jucy-ui/appearance';
import { BlockTitle } from '@/components/Blocks/components/BlockTitle';
import { SearchFormCardLarge } from '@/components/SearchForm/SearchFormCardLarge';
import { useSearchFormContainerQuery } from '@/components/SearchForm/hooks/useSearchFormContainerQuery';
import { SiteContainer } from '@/components/SiteContainer';
import { VideoBanner } from '@/components/VideoBanner/VideoBanner';
import { CmsHeroBlock } from '@/services/cms/blocks/CmsHeroBlock';


export type HeroBlockProps = CmsHeroBlock & {
    sx?: SxProps<Theme>;
    children?: React.ReactNode;
};

export const HeroBlock = ({ background, video, sx, showSearchForm, children, ...props }: HeroBlockProps) => {
    const { ref } = useParallax<HTMLDivElement>({
        translateY: ['0px', '-100px'],
        shouldAlwaysCompleteAnimation: true,
    });
    const smallContainerQuery = useSearchFormContainerQuery();
    return (
        <Box
            sx={mergeSx(
                (theme) => ({
                    position: 'relative',
                    height: `${theme.settings.bannerHeights.large}px`,
                    display: 'flex',
                    alignItems: 'center',
                    ...(background?.link && !video?.playbackId
                        ? {
                              backgroundImage: `url(${background.link})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center center',
                          }
                        : undefined),
                    [smallContainerQuery]: {
                        height: 'auto',
                        backgroundImage: 'none',
                        '& .bannerVideo': {
                            display: 'none',
                        },
                    },
                }),
                sx
            )}
        >
            {video?.playbackId ? (
                <VideoBanner
                    sx={{ top: 0, bottom: 0, position: 'absolute', width: '100%', zIndex: -1 }}
                    className="bannerVideo"
                    size="large"
                    useTexture={true}
                    poster={background?.link}
                    playbackId={video?.playbackId}
                />
            ) : null}
            <SiteContainer ref={ref}>
                {children}
                {showSearchForm ? (
                    <SearchFormCardLarge>
                        <BlockTitle {...props} />
                    </SearchFormCardLarge>
                ) : null}
            </SiteContainer>
        </Box>
    );
};
